import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, SearchIcon } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { toast } from "sonner";
import { type TableSort, useTableSort } from "@/core/hooks/use-table-sort";
import {
  type AuditCategory,
  type AuditEntry,
  type AuditSortKey,
  downloadAuditLog,
  fetchAdminAuditLog,
  fetchAuditLog,
} from "@/core/lib/audit-api";
import { cn } from "@/core/lib/utils";
import { SortableTableHead } from "@/ui/components/common/sortable-table-head";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NativeSelect, NativeSelectOption } from "../ui/native-select";
import { Skeleton } from "../ui/skeleton";
import { Spinner } from "../ui/spinner";
import { Switch } from "../ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const PAGE_SIZE = 50;
const SKELETON_ROWS = ["s0", "s1", "s2", "s3", "s4", "s5", "s6", "s7"];

const CATEGORY_LABEL: Record<AuditCategory, string> = {
  data: "Data change",
  auth: "Authentication",
  access: "Access",
  admin: "Admin",
};

const CATEGORY_CLASS: Record<AuditCategory, string> = {
  data: "border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400",
  auth: "border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400",
  access: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  admin: "border-rose-500/30 bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

/** "employee.update" → "Employee update" */
function actionLabel(action: string): string {
  const text = action.replace(/[._]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatWhen(iso: string): { rel: string; abs: string } {
  const date = new Date(iso);
  const abs = date.toLocaleString();
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return { rel: "just now", abs };
  if (min < 60) return { rel: `${min}m ago`, abs };
  const hr = Math.round(min / 60);
  if (hr < 24) return { rel: `${hr}h ago`, abs };
  const day = Math.round(hr / 24);
  if (day < 7) return { rel: `${day}d ago`, abs };
  return { rel: date.toLocaleDateString(), abs };
}

function valuePreview(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export type AuditLogParams = {
  query: string;
  category: string;
  includeArchived: boolean;
  page: number;
  sort?: TableSort<AuditSortKey>;
};

/** The view's opening state — what a route loader warms. */
export const DEFAULT_AUDIT_LOG_PARAMS: AuditLogParams = {
  query: "",
  category: "all",
  includeArchived: false,
  page: 0,
};

/** One page of the log, shared by the view and the route that warms it. */
export const auditLogQuery = (scope: "workspace" | "platform", params: AuditLogParams) =>
  queryOptions({
    queryKey: [
      "audit-log",
      scope,
      params.query,
      params.category,
      params.includeArchived,
      params.page,
      params.sort ?? null,
    ],
    queryFn: () =>
      (scope === "platform" ? fetchAdminAuditLog : fetchAuditLog)({
        q: params.query || undefined,
        category: params.category,
        includeArchived: scope === "platform" ? params.includeArchived : undefined,
        limit: PAGE_SIZE,
        offset: params.page * PAGE_SIZE,
        sort: params.sort?.key,
        dir: params.sort?.dir,
      }),
  });

/**
 * Read-only audit-log table with text search, category filter, paging, and a
 * per-row detail expander (field diffs + request metadata). Shared by the HR
 * (`scope="workspace"`) and platform-admin (`scope="platform"`) viewers; the
 * platform view adds a Workspace column.
 */
export function AuditLogView({ scope }: { scope: "workspace" | "platform" }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const { sort, toggle } = useTableSort<AuditSortKey>(null, { when: "desc" });
  const toggleSort = (key: AuditSortKey) => {
    toggle(key);
    setPage(0);
  };

  const { data, isLoading, isError, error, isFetching } = useQuery({
    ...auditLogQuery(scope, { query, category, includeArchived, page, sort }),
    placeholderData: keepPreviousData,
  });

  const entries = data?.items ?? [];
  const total = data?.total ?? 0;
  const colSpan = scope === "platform" ? 6 : 5;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(0);
    setQuery(search);
  }

  async function exportLog() {
    setIsExporting(true);
    try {
      await downloadAuditLog(scope, {
        q: query || undefined,
        category,
        includeArchived: scope === "platform" ? includeArchived : undefined,
      });
      toast.success("Activity log exported");
    } catch (err) {
      toast.error("Couldn't export activity log", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="relative flex-1 min-w-56">
          <SearchIcon className="-translate-y-1/2 absolute top-1/2 left-2.5 size-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search action, actor, or record…"
            className="pl-8"
          />
        </form>
        <NativeSelect
          value={category}
          onChange={(e) => {
            setPage(0);
            setCategory(e.target.value);
          }}
          className="w-44"
        >
          <NativeSelectOption value="all">All categories</NativeSelectOption>
          <NativeSelectOption value="data">Data changes</NativeSelectOption>
          <NativeSelectOption value="auth">Authentication</NativeSelectOption>
          <NativeSelectOption value="access">Access</NativeSelectOption>
          <NativeSelectOption value="admin">Admin</NativeSelectOption>
        </NativeSelect>
        {scope === "platform" ? (
          <label
            htmlFor="audit-include-archived"
            className="flex items-center gap-2 text-muted-foreground text-sm"
          >
            <Switch
              id="audit-include-archived"
              checked={includeArchived}
              onCheckedChange={(v) => {
                setPage(0);
                setIncludeArchived(v);
              }}
            />
            Show archived
          </label>
        ) : null}
        <Button
          type="button"
          variant="outline"
          className="ml-auto"
          disabled={isLoading || isError || isExporting || total === 0}
          onClick={exportLog}
        >
          {isExporting ? <Spinner className="size-4" /> : <DownloadIcon className="size-4" />}
          Export CSV
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead column="when" sort={sort} onSort={toggleSort} className="w-36">
                When
              </SortableTableHead>
              <SortableTableHead column="action" sort={sort} onSort={toggleSort}>
                Action
              </SortableTableHead>
              <SortableTableHead column="actor" sort={sort} onSort={toggleSort}>
                Actor
              </SortableTableHead>
              <SortableTableHead column="record" sort={sort} onSort={toggleSort}>
                Record
              </SortableTableHead>
              {scope === "platform" ? <TableHead>Workspace</TableHead> : null}
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              SKELETON_ROWS.map((key) => (
                <TableRow key={key}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="py-10 text-center text-destructive text-sm">
                  {(error as Error)?.message ?? "Couldn't load the audit log."}
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="py-10 text-center text-muted-foreground text-sm"
                >
                  No audit entries match these filters yet.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  scope={scope}
                  colSpan={colSpan}
                  open={expanded === entry.id}
                  onToggle={() => setExpanded((cur) => (cur === entry.id ? null : entry.id))}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-muted-foreground text-sm">
        <span>
          {total > 0
            ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`
            : "0 entries"}
          {isFetching ? " · refreshing…" : ""}
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function AuditRow({
  entry,
  scope,
  colSpan,
  open,
  onToggle,
}: {
  entry: AuditEntry;
  scope: "workspace" | "platform";
  colSpan: number;
  open: boolean;
  onToggle: () => void;
}) {
  const when = useMemo(() => formatWhen(entry.createdAt), [entry.createdAt]);
  const changeKeys = entry.changes ? Object.keys(entry.changes) : [];
  // The workspace (user) viewer keeps rows collapsed — no request-metadata
  // expander. Only the platform-admin viewer surfaces the detail section.
  const hasDetail =
    scope === "platform" &&
    (changeKeys.length > 0 ||
      (entry.metadata && Object.keys(entry.metadata).length > 0) ||
      !!entry.ip);

  return (
    <Fragment>
      <TableRow
        className={cn(hasDetail && "cursor-pointer")}
        onClick={hasDetail ? onToggle : undefined}
      >
        <TableCell className="text-muted-foreground text-sm" title={when.abs}>
          {when.rel}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("font-medium", CATEGORY_CLASS[entry.category])}>
              {CATEGORY_LABEL[entry.category] ?? entry.category}
            </Badge>
            <span className="font-medium text-sm">{actionLabel(entry.action)}</span>
            {entry.status === "failure" ? <Badge variant="destructive">failed</Badge> : null}
          </div>
        </TableCell>
        <TableCell className="text-sm">
          <span className="font-medium">{entry.actorName ?? "—"}</span>
          {entry.actorRole ? (
            <span className="ml-1 text-muted-foreground text-xs">({entry.actorRole})</span>
          ) : null}
        </TableCell>
        <TableCell className="text-sm">
          {entry.targetLabel ? (
            <span>{entry.targetLabel}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
          {entry.targetType ? (
            <span className="ml-1 text-muted-foreground text-xs">{entry.targetType}</span>
          ) : null}
        </TableCell>
        {scope === "platform" ? (
          <TableCell className="text-muted-foreground text-xs">
            {entry.ownerType === "platform"
              ? "Platform"
              : `${entry.ownerType}:${entry.ownerId.slice(0, 8)}`}
          </TableCell>
        ) : null}
        <TableCell>
          {hasDetail ? (
            open ? (
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="size-4 text-muted-foreground" />
            )
          ) : null}
        </TableCell>
      </TableRow>
      {open && hasDetail ? (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={colSpan} className="space-y-3 py-3">
            {changeKeys.length > 0 ? (
              <div>
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                  Changes
                </p>
                <div className="space-y-1">
                  {changeKeys.map((key) => {
                    const change = entry.changes?.[key];
                    return (
                      <div key={key} className="flex flex-wrap items-baseline gap-2 text-sm">
                        <span className="font-medium">{key}</span>
                        <span className="text-muted-foreground line-through">
                          {valuePreview(change?.from)}
                        </span>
                        <ChevronRightIcon className="size-3 text-muted-foreground" />
                        <span>{valuePreview(change?.to)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground text-xs">
              {entry.ip ? <span>IP: {entry.ip}</span> : null}
              {entry.metadata &&
                Object.entries(entry.metadata).map(([k, v]) => (
                  <span key={k}>
                    {k}: {valuePreview(v)}
                  </span>
                ))}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}
